<template>
	<div class="list-group no-border border-radius-2 mb-8 position-relative">
		<!-- Filters -->
		<div :class="{ 'position-sticky sticky-top': scrollPaginate }" class="pt-1 pt-md-0">
			<slot name="header" />

			<!-- The fallback search component below should be preferred, but can be over-ridden if needed -->
			<slot name="search">
				<div v-if="searchable" class="list-group-item d-flex flex-row flex-wrap align-items-center">
					<SearchBar :value="filters.search" @input="value => refresh({ search: value })" />

					<slot name="options" />
				</div>
			</slot>
		</div>

		<!-- Table Headers -->
		<div :class="`list-group-item d-none d-${breakpoint}-inline ${headerClass}`">
			<div v-if="filters" class="row">
				<div
					v-for="col in cols"
					:class="`col-xs-12 col-${breakpoint}-${col.size} font-bold`"
					:key="col.key"
				>
					<div
						@click="!submitting && col.sortable ? refresh({
							sort: col.sortCol ? col.sortCol : col.key,
							order: (col.sortCol ? col.sortCol : col.key) !== filters.sort ? 'ASC' : filters.order === 'DESC' ? 'ASC' : 'DESC'
						}) : undefined"
						:class="`d-none d-${breakpoint}-flex flex-row align-items-center`"
					>
						<div class="mr-2">{{ col.heading }}</div>
						<b-icon v-if="filters.sort === (col.sortCol ? col.sortCol : col.key)" :icon="filters.order === 'DESC' ? 'caret-up' : 'caret-down'" />
					</div>

					<div :class="`d-${breakpoint}-none mr-2`">{{ col.heading }}</div>
				</div>
			</div>
		</div>

		<!-- Loader -->
		<template v-if="loading">
			<SkeletonRow
				v-for="(item, ind) in Array(skeletonLength || 10)"
				v-bind:key="ind"
				:breakpoint="breakpoint"
				:cols="cols"
			/>
		</template>

		<!-- Table -->
		<template v-else-if="scrollPaginate">
			<ScrollPaginator
				:data="data"
				:submitting="submitting"
				:filters="filters"
				@toPage="toPage"
				ref="scrollPaginator"
			>
				<template v-slot:item="{ item }">
					<TableRow 
						:item="item" 
						:cols="cols" 
						breakpoint="md" 
						@click="$emit('selectItem', item)"
						@edit="$emit('editItem', item)"
						@delete="$emit('deleteItem', item)"
						:showEdit="rowEdit"
						:showDelete="rowDelete"
                        :allowRightClick="allowRightClick"
						:rowClass="rowClass"
					>

                        <template v-for="(col, index) in cols" #[`col-${col.key}`]="slotProps">
                            <slot :name="`col-${col.key}`" v-bind="slotProps" />
                        </template>
                        
						<template v-slot:actions>
							<slot name="rowActions" :item="item">
                            </slot>
						</template>
					</TableRow>
				</template>
				<template v-slot:skeletonLoader>
					<SkeletonRow :cols="cols" breakpoint="md" />
				</template>
			</ScrollPaginator>
		</template>

		<template v-else>

			<!-- Table Data -->
			<TableRow
				v-for="item in data"
				v-bind:key="item.key"
				:item="item"
				:cols="cols"
				breakpoint="md"
				@click="$emit('selectItem', item)"
				ref="rowElements"
                :allowRightClick="allowRightClick"
				:rowClass="rowClass"
			>
            <!--Forwarding-->
            <template v-for="(col, index) in cols" #[`col-${col.key}`]="slotProps">
                <slot 
                :name="`col-${col.key}`" 
                v-bind="slotProps"
                ></slot>
            </template>

            <template #actions>
                <slot name="rowActions" :item="item"></slot>
            </template>
        
            </TableRow>

			<!-- Traditional Paginator -->
			<Paginator
				v-if="filters.page && filters.page_size && filters.total"
				:page="filters.page"
				:pageSize="filters.page_size"
				:total="filters.total"
				@toPage="toPage"
			/>

		</template>

	</div>
</template>

<script>
import Paginator from './Paginator.vue';
import ScrollPaginator from './ScrollPaginator.vue';
import SkeletonRow from './SkeletonRow.vue';
import SearchBar from './SearchBar.vue';
import TableRow from './TableRow.vue';

export default {
    props: {
		/*
			Example col object:

			{
				key: 		The data field to show in this column,
				heading: 	The column heading,
				size: 		The number of bootstrap cols this field should use,
				sortable: 	If true, allows user to sort by this column,
				sortCol: 	Use if the DB column you want to sort does not have the same name as the key provided (col names are case-insensitive in MySQL, MSSQL),
				cb: 		If you wish to display a value derived from multiple fields, pass a callback fn taking an item of data as its arg,
			}
		*/
		cols: Array,
        data: Array,
        searchable: Boolean,
        breakpoint: {
			type: String,
			default: "md",
		},
        loading: Boolean,
        submitting: Boolean,
		scrollPaginate: {
			type: Boolean,
			default: true,
		},
        skeletonLength: Number,
        filters: { 	// If paginating, should include page, page_size, total_count. Example below
            type: Object,
            default: () => { return {
				page: undefined,
				page_size: undefined,
				total: undefined,
			} },
        },
		rowEdit: Boolean,
		rowDelete: Boolean,
		allowRightClick: {
			type: Boolean,
			default: false,
		},
		headerClass: {
			type: String,
			default: '',
		},
		rowClass: {
			type: String,
			default: '',
		}
    },
    data() {
        return {
			rows: [],
        };
    },
    methods: {
        toPage: function (pageNo) {
            if (this.submitting) return
            this.$emit('toPage', pageNo);
        },
		refresh: function (filters) {
			this.$emit('refresh', { ...filters, page: 1 })
		},
    },
    computed: {
        pageCount() {
			const count = Math.ceil(this.filters.total / this.filters.page_size)
            return count > 0 ? count : 1
        },
    },
    components: { Paginator, ScrollPaginator, SkeletonRow, SearchBar, TableRow }
};
</script>
